import {useMutation} from '@apollo/client'
import {useEffect, useState, FormEvent} from 'react'
import {Button, Modal, Spinner, Form} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {UPDATE_COMMISSION_REPORT_LOG} from '../../../gql/mutations/commissionReportLogMutations'
import {
  IResUpdateCommissionReportLog,
  NodeCommissionReportLog,
} from '../../../types/commissionReportLogApi'
import {parseId} from '../../../helpers'

interface IModalEditLogProps {
  isOpen: boolean
  onClose: () => void
  infoEdit: NodeCommissionReportLog | null
  refetch: () => void
}

export const ModalEditCommissionReportLog: React.FC<IModalEditLogProps> = ({
  isOpen,
  onClose,
  infoEdit,
  refetch,
}) => {
  const [formData, setFormData] = useState({
    client: '',
    memberId: '',
    rate: '',
    applicants: '',
    commissionReport: '',
    comments: '',
    status: '',
    monthPaid: new Date().getMonth() + 1,

  })

  const [gqlUpdate, {loading, data, error}] = useMutation<IResUpdateCommissionReportLog>(
    UPDATE_COMMISSION_REPORT_LOG
  )

  useEffect(() => {
    if (infoEdit) {
      setFormData({
        client: infoEdit.client ?? '',
        memberId: infoEdit.memberId ?? '',
        rate: infoEdit.rate ?? 0,
        applicants: (infoEdit.applicants ?? 1).toString(),
        commissionReport: infoEdit.commissionReport?.id ?? '',
        comments: infoEdit.comments ?? '',
        status: infoEdit.status ?? 'NOT_FOUND',
        monthPaid: infoEdit.monthPaid ?? new Date().getMonth() + 1,
      })
    }
  }, [infoEdit])


  useEffect(() => {
    if (!data) return
    const res = data.commissionReportLogUpdateMutation
    if (res?.success) {
      toast.success('Commission Report Log updated successfully')
      refetch()
      onClose()
    } else if (res?.errors?.length) {
      toast.error(`Error: ${res.errors[0]?.message}`)
    }
  }, [data])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({...prev, [field]: value}))
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!infoEdit) return

    gqlUpdate({
      variables: {
        id: parseId(infoEdit.id),
        input: {
          ...formData,
          commissionReport: parseId(formData.commissionReport),
        },
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Update report log</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label>Client</Form.Label>
            <Form.Control
              type='text'
              value={formData.client}
              onChange={e => handleChange('client', e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Commission Status</Form.Label>
            <Form.Select
              value={formData.status}
              onChange={e => handleChange('status', e.target.value)}
              required
            >
              <option value=''>Select type</option>
              <option value='APPROVED'>Approved</option>
              <option value='APPROVED_MANUALLY'>Approved manually</option>
              <option value='APPROVED_DISCREPANCY'>Approved discrepancy</option>
              <option value='APPROVED_MANUALLY_DISCREPANCY'>Approved manually discrepancy</option>
              <option value='NOT_FOUND'>Not found</option>

            </Form.Select>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Member ID</Form.Label>
            <Form.Control
              type='text'
              value={formData.memberId}
              onChange={e => handleChange('memberId', e.target.value)}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Rate (%)</Form.Label>
            <Form.Control
              type='number'
              step='0.01'
              value={formData.rate}
              onChange={e => handleChange('rate', e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Quantity of applicants</Form.Label>
            <Form.Control
              type='number'
              value={formData.applicants}
              onChange={e => handleChange('applicants', e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>Comments</Form.Label>
            <Form.Control
              type='text'
              value={formData.comments}
              onChange={e => handleChange('comments', e.target.value)}
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClose}>
            CLOSE
          </Button>
          <Button type='submit' variant='primary' disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            SAVE
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
